import { useState } from 'react';
import { FLEET_BOARD_ALL_VIEW, type FleetBoardCard, type GetFleetBoardResponse } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { BoardColumn } from '@/components/fleet/board/board-column';
import type { QueueRanking } from '@/components/fleet/board/use-queue-ranking';

/** One lane per column: 280 px wide, 300 px on a phone where the board snaps to the next lane. */
export const FLEET_BOARD_GRID_CLASS =
  'grid w-max min-w-full auto-cols-[300px] grid-flow-col items-start gap-3 snap-x snap-mandatory sm:auto-cols-[280px]';

type Props = {
  readonly board: GetFleetBoardResponse;
  readonly now: number;
  readonly onOpen?: ((card: FleetBoardCard) => void) | undefined;
  readonly onWorkers?: ((lane: string) => void) | undefined;
  /** Absent when the operator may not reorder queues. */
  readonly ranking?: Pick<QueueRanking, 'busy' | 'onDragStart' | 'onDragCancel' | 'onMove'> | undefined;
};

/** Renders the backend's answer as it is: its lanes in declaration order, each lane's cards where the backend put them (or the operator just moved them). */
export function FleetBoard({ board, now, onOpen, onWorkers, ranking }: Props) {
  const t = useTranslations('pages.admin.fleet-board');
  const showProject = board.view === FLEET_BOARD_ALL_VIEW;
  // No top padding: the lane headers rest where they stick, so scrolling never moves them — only their shadow appears.
  const [scrolled, setScrolled] = useState(false);

  return (
    <div
      data-scrolled={scrolled ? '' : undefined}
      onScroll={(event) => setScrolled(event.currentTarget.scrollTop > 0)}
      className="group/board h-full min-h-0 overflow-auto bg-canvas px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div role="group" aria-label={t('columns')} className={FLEET_BOARD_GRID_CLASS}>
        {board.lanes.map((lane) => (
          <BoardColumn
            key={lane.name}
            lane={lane}
            showProject={showProject}
            now={now}
            onOpen={onOpen}
            onWorkers={onWorkers}
            ranking={
              ranking === undefined
                ? undefined
                : {
                    busy: ranking.busy.has(lane.name),
                    onDragStart: ranking.onDragStart,
                    onDragCancel: ranking.onDragCancel,
                    onMove: ranking.onMove,
                  }
            }
          />
        ))}
      </div>
    </div>
  );
}
